import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Table } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Trash2, FileText, X } from "lucide-react";
import { api } from "@/lib/api";
import { DetailHeader } from "@/components/layout/detail-header";
import { useToast } from "@/contexts/toast-context";
import { ConfirmationModal } from "@/components/ui/confirmation-modal";

interface Log {
  id: string;
  description: string;
  stack: string;
  createdAt: string;
  level?: string;
}

export function LogsPage() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [logs, setLogs] = useState<Log[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<Log[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [showDeleteSingleModal, setShowDeleteSingleModal] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [showStackModal, setShowStackModal] = useState<Log | null>(null);

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    try {
      setIsLoading(true);
      const response = await api.get<Log[]>("/logs");
      const logsData = Array.isArray(response) ? response : [];
      setLogs(logsData);
      setFilteredLogs(logsData);
    } catch (error) {
      console.error("Failed to load logs:", error);
      setLogs([]);
      setFilteredLogs([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter logs based on search
  useEffect(() => {
    if (!searchValue.trim()) {
      setFilteredLogs(logs);
    } else {
      const filtered = logs.filter(log => 
        log.description.toLowerCase().includes(searchValue.toLowerCase()) ||
        (log.level?.toLowerCase().includes(searchValue.toLowerCase())) ||
        log.stack.toLowerCase().includes(searchValue.toLowerCase()) ||
        formatTimestamp(log.createdAt).toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredLogs(filtered);
    }
  }, [logs, searchValue]);

  const handleDeleteLog = async (logId: string) => {
    try {
      setIsDeleting(logId);
      await api.delete(`/logs/${logId}`);
      setLogs(logs.filter(log => log.id !== logId));
      setShowDeleteSingleModal(null);
      showSuccess("Log deleted successfully", "The log entry has been removed");
    } catch (error) {
      console.error("Failed to delete log:", error);
      showError("Failed to delete log", "Could not delete the log entry. Please try again.");
    } finally {
      setIsDeleting(null);
    }
  };

  const handleDeleteAllLogs = async () => {
    try {
      const logCount = logs.length;
      await api.delete("/logs");
      setLogs([]);
      setShowDeleteAllModal(false);
      showSuccess("All logs deleted", `Successfully deleted ${logCount} log entries`);
    } catch (error) {
      console.error("Failed to delete all logs:", error);
      showError("Failed to delete logs", "Could not delete all logs. Please try again.");
    }
  };

  const formatTimestamp = (timestamp: string) => {
    if (!timestamp) return "Invalid date";
    
    try {
      const date = new Date(timestamp);
      if (Number.isNaN(date.getTime())) {
        return "Invalid date";
      }
      return date.toLocaleString();
    } catch (error) {
      return "Invalid date";
    }
  };

  const breadcrumb = [
    { label: "Admin" },
    { label: "Logs" }
  ];

  const toggleOptions = [
    { id: "logs", label: "Logs", path: "/app/admin/logs" },
    { id: "users", label: "Users", path: "/app/admin/users" }
  ];

  const columns = [
    {
      key: "createdAt" as keyof Log,
      header: "Timestamp",
      width: 48,
      render: (log: Log) => (
        <div className="text-xs text-muted-foreground">
          {formatTimestamp(log.createdAt)}
        </div>
      )
    },
    {
      key: "level" as keyof Log,
      header: "Level", 
      width: 20,
      render: (log: Log) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          log.level === "error" ? "bg-destructive/10 text-destructive" :
          log.level === "warn" ? "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300" :
          "bg-muted text-muted-foreground"
        }`}>
          {log.level || "info"}
        </span>
      )
    },
    {
      key: "description" as keyof Log,
      header: "Description",
      width: 240,
      render: (log: Log) => (
        <div className="text-xs font-medium truncate pr-2" title={log.description}>
          {log.description}
        </div>
      )
    },
    {
      key: "actions",
      header: "",
      width: 24,
      className: "text-right",
      render: (log: Log) => (
        <div className="flex items-center justify-end gap-1">
          {log.stack && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowStackModal(log)}
              className="text-muted-foreground hover:text-foreground h-8 w-8 p-0"
              title="View stack trace"
            >
              <FileText className="w-4 h-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowDeleteSingleModal(log.id)}
            disabled={isDeleting === log.id}
            className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
            title="Delete log"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )
    }
  ];

  const actions = logs.length > 0 ? (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => setShowDeleteAllModal(true)}
      className="text-destructive hover:text-destructive hover:bg-destructive/10 text-xs"
    >
      <Trash2 className="w-3 h-3 mr-1" />
      Delete All
    </Button>
  ) : undefined;

  return (
    <div className="flex flex-col h-full">
      <DetailHeader
        breadcrumb={breadcrumb}
        toggleOptions={toggleOptions}
        activeToggle="logs"
        onToggleChange={(toggleId) => {
          const option = toggleOptions.find(opt => opt.id === toggleId);
          if (option) navigate(option.path);
        }}
        actions={actions}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder="Search logs..."
      />
      
      <div className="flex-1 overflow-hidden">
        <Table
          data={filteredLogs}
          columns={columns}
          isLoading={isLoading}
          emptyMessage="No logs found"
          fixedHeader={true}
        />
      </div>

      {/* Delete All Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteAllModal}
        onClose={() => setShowDeleteAllModal(false)}
        onConfirm={handleDeleteAllLogs}
        title="Delete All Logs"
        message="Are you sure you want to delete all logs? This action cannot be undone."
        confirmText="Delete All"
        type="danger"
      />

      {/* Delete Single Log Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!showDeleteSingleModal}
        onClose={() => setShowDeleteSingleModal(null)}
        onConfirm={() => showDeleteSingleModal && handleDeleteLog(showDeleteSingleModal)}
        title="Delete Log"
        message="Are you sure you want to delete this log entry? This action cannot be undone."
        confirmText="Delete"
        type="danger"
        isLoading={isDeleting === showDeleteSingleModal}
      />

      {/* Stack Trace Modal */}
      {showStackModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background border border-border rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Stack Trace</h3>
              <button
                onClick={() => setShowStackModal(null)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Description */}
            <div className="mb-4 p-3 bg-muted rounded-md">
              <div className="text-sm font-medium mb-1">Error:</div>
              <div className="text-sm">{showStackModal.description}</div>
            </div>
            
            {/* Stack Trace */}
            <div className="flex-1 overflow-auto">
              <div className="text-sm font-medium mb-2">Stack Trace:</div>
              <pre className="text-xs bg-muted p-4 rounded-md overflow-auto font-mono whitespace-pre-wrap break-words">
                {showStackModal.stack}
              </pre>
            </div>
            
            {/* Close Button */}
            <div className="flex justify-end pt-4">
              <Button
                onClick={() => setShowStackModal(null)}
                size="sm"
                className="text-xs"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
